import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  VoyageRequestsService,
  VoyageRequestItem,
  RequestQueryParams,
} from 'src/app/shared/service/voyage-requests/voyage-requests.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

@Component({
  selector: 'app-voyagerequest-list',
  templateUrl: './voyagerequest-list.component.html',
  styleUrls: ['./voyagerequest-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VoyagerequestListComponent implements OnInit, OnDestroy {
  private static readonly BODY_SCROLL_CLASS = 'voyage-request-list-scroll';
  private destroy$ = new Subject<void>();

  requestsList: VoyageRequestItem[] = [];
  totalRecords = 0;
  activeCount = 0;
  draftCount = 0;
  selectedTab: 'active' | 'drafts' = 'active';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  selectedClient = 'ALL';
  selectedVessel = 'ALL';
  selectedStatus = 'ALL';
  searchTerm = '';
  sortBy: 'createdAt' | 'vesselName' | 'clientName' | 'status' | 'departureDate' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  clientOptions: string[] = [];
  vesselOptions: string[] = [];
  statusOptions: string[] = [
    'ALL',
    'Draft',
    'Requested',
    'Under Review',
    'In Progress',
    'Edit Requested',
    'Created',
    'Completed',
    'Cancelled',
  ];

  isLoading = false;
  expandedRowId: string | null = null;
  activeDrawerTab: 'timeline' | 'details' = 'timeline';

  // Request Customer Edit Modal / Popover State
  updatingRequestIds = new Set<string>();
  isRequestingEditModalOpen = false;
  selectedRequestForEditModal: VoyageRequestItem | null = null;
  customerEditPromptNote = '';

  private sub = new Subscription();

  get isMerakiUser(): boolean {
    return localStorage.getItem('username') === 'Meraki - Operations';
  }

  get currentUser(): string {
    return localStorage.getItem('username') || '';
  }

  get pageTitle(): string {
    return this.isMerakiUser ? "Client Requests" : "My Requests";
  }

  get subtitle(): string {
    return this.isMerakiUser
      ? "Manage and monitor all routing requests submitted by clients, inspect interactive audit logs, and trigger routing"
      : "Track live status, milestones, and audit history of your submitted voyage routing requests";
  }

  constructor(
    private voyageRequestsService: VoyageRequestsService,
    private requestFormService: RequestFormService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    document.body.classList.add(VoyagerequestListComponent.BODY_SCROLL_CLASS);
    this.loadRequests();

    this.sub.add(
      this.voyageRequestsService.prefillData$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {})
    );
  }

  ngOnDestroy(): void {
    document.body.classList.remove(VoyagerequestListComponent.BODY_SCROLL_CLASS);
    this.sub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRequests(): void {
    this.isLoading = true;
    const params: RequestQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      tabMode: this.selectedTab,
      clientFilter: this.isMerakiUser ? this.selectedClient : this.currentUser,
      vesselFilter: this.selectedVessel,
      statusFilter: this.selectedTab === 'drafts' ? 'Draft' : this.selectedStatus,
      searchTerm: this.searchTerm,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    };

    this.voyageRequestsService
      .queryRequests(params, this.currentUser, this.isMerakiUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.requestsList = res.data.map(item => ({
            ...item,
            isExpanded: item.id === this.expandedRowId
          }));
          this.totalRecords = res.total;
          this.activeCount = res.activeCount ?? 0;
          this.draftCount = res.draftCount ?? 0;
          this.clientOptions = res.clients;
          this.vesselOptions = res.vessels;
          this.isLoading = false;
        },
        error: () => {
          this.toastr.error('Failed to load voyage requests');
          this.isLoading = false;
        },
      });
  }

  onRowClick(item: VoyageRequestItem, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.action-btn-group') || target.closest('button') || target.closest('mat-select') || target.closest('a')) {
      return;
    }
    this.toggleRowExpansion(item);
  }

  toggleTimeline(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.activeDrawerTab = 'timeline';
    this.toggleRowExpansion(item);
  }

  toggleRowExpansion(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.expandedRowId === item.id && item.isExpanded) {
      this.expandedRowId = null;
      item.isExpanded = false;
    } else {
      this.expandedRowId = item.id;
      this.requestsList.forEach(r => r.isExpanded = (r.id === item.id));
      item.isExpanded = true;
      // Refresh single request timeline details
      this.voyageRequestsService.getRequestById(item.id).subscribe({
        next: (res) => {
          if (res?.success && res.data) {
            Object.assign(item, res.data);
          }
        }
      });
    }
  }

  closeDrawer(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.expandedRowId = null;
    item.isExpanded = false;
  }

  setDrawerTab(tab: 'timeline' | 'details'): void {
    this.activeDrawerTab = tab;
  }

  setTab(tab: 'active' | 'drafts'): void {
    if (this.selectedTab === tab) return;
    this.selectedTab = tab;
    this.pageIndex = 0;
    this.selectedStatus = 'ALL';
    this.loadRequests();
  }

  get displayedStatusOptions(): string[] {
    if (this.selectedTab === 'drafts') {
      return ['Draft'];
    }
    return this.statusOptions.filter(s => s !== 'Draft');
  }

  onSearchChange(): void {
    this.pageIndex = 0;
    this.loadRequests();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.loadRequests();
  }

  toggleSort(field: 'createdAt' | 'vesselName' | 'clientName' | 'status' | 'departureDate'): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'desc';
    }
    this.loadRequests();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRequests();
  }

  updateStatus(item: VoyageRequestItem, newStatus: any, remarks?: string): void {
    if (this.updatingRequestIds.has(item.id)) return;
    this.updatingRequestIds.add(item.id);

    this.voyageRequestsService.updateStatus(item.id, newStatus, remarks).subscribe({
      next: () => {
        this.updatingRequestIds.delete(item.id);
        this.toastr.success(`Request ${item.id} status updated to '${newStatus}'`);
        this.loadRequests();
      },
      error: (err) => {
        this.updatingRequestIds.delete(item.id);
        console.error('Status update failed:', err);
        this.toastr.error(`Could not update status of request ${item.id}`);
      }
    });
  }

  startRouting(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.updateStatus(item, 'In Progress', 'Meraki Operations started routing analysis');
  }

  openCustomerEditModal(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.selectedRequestForEditModal = item;
    this.customerEditPromptNote = '';
    this.isRequestingEditModalOpen = true;
  }

  closeCustomerEditModal(): void {
    this.isRequestingEditModalOpen = false;
    this.selectedRequestForEditModal = null;
    this.customerEditPromptNote = '';
  }

  confirmCustomerEditRequest(): void {
    if (!this.selectedRequestForEditModal) return;

    const payload = {
      action: 'REQUEST_CUSTOMER_EDIT',
      remarks: this.customerEditPromptNote.trim() || 'Meraki Operations requested clarification on routing parameters.'
    };

    this.voyageRequestsService.updateRequest(this.selectedRequestForEditModal.id, payload).subscribe({
      next: () => {
        this.toastr.success(
          `Customer edit requested for ${this.selectedRequestForEditModal?.id}`,
          'Edit Requested'
        );
        this.closeCustomerEditModal();
        this.loadRequests();
      },
      error: () => {
        this.toastr.error('Failed to request edit from customer', 'Error');
      }
    });
  }

  editRequest(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.router.navigate(['/routing-request'], {
      queryParams: { editId: item.id }
    });
  }

  downloadRouteCsv(item: VoyageRequestItem, event?: Event): void {
    if (event) event.stopPropagation();
    const csvData = item.rawPayload?.routeCsvData || (item as any).routeCsvData;
    const fileName = item.routeCsvName || item.rawPayload?.routeCsvName || 'voyage_route.csv';

    if (csvData) {
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.toastr.success(`Downloaded ${fileName}`, 'File Downloaded');
    } else {
      this.voyageRequestsService.getRequestById(item.id).subscribe({
        next: (res) => {
          const fullData = res?.data?.routeCsvData || res?.data?.rawPayload?.routeCsvData;
          if (fullData) {
            const blob = new Blob([fullData], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.toastr.success(`Downloaded ${fileName}`, 'File Downloaded');
          } else {
            this.toastr.info('No CSV content found for this request.', 'File Unavailable');
          }
        },
        error: () => {
          this.toastr.error('Could not download route CSV file.', 'Download Error');
        }
      });
    }
  }

  downloadReport(item: VoyageRequestItem, eventItem?: any, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    // Log the download event to the timeline
    this.voyageRequestsService.logEvent(item.id, {
      action: 'REPORT_DOWNLOADED',
      title: 'Route Optimization Report Downloaded',
      remarks: `Report downloaded by ${this.currentUser || 'User'}`
    }).subscribe({
      next: () => {
        this.toastr.success(`Downloading Route Report for ${item.vesselName}...`, 'Report Download');
      },
      error: () => {
        this.toastr.info(`Downloading Route Report for ${item.vesselName}...`, 'Report Download');
      }
    });
  }

  formatTimeAgoOrResolved(item: VoyageRequestItem): { text: string; isResolved: boolean } {
    return this.voyageRequestsService.formatTimeAgoOrResolved(
      item.createdAt,
      item.resolvedAt,
      item.status,
      item.statusHistory,
      item.submittedAt
    );
  }

  formatRelativeTime(isoString?: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 2) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  openInInitialRouteReport(item: VoyageRequestItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.voyageRequestsService.setPrefillForInitialRoute(item);
    this.router.navigate(['/vp-requestForm'], {
      queryParams: {
        type: 'initial',
        fromRequestId: item.id
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Draft':
        return 'status-draft';
      case 'Requested':
        return 'status-requested';
      case 'Resubmitted':
        return 'status-resubmitted';
      case 'Under Review':
        return 'status-review';
      case 'In Progress':
        return 'status-progress';
      case 'Edit Requested':
        return 'status-edit-requested';
      case 'Created':
        return 'status-created';
      case 'Completed':
        return 'status-completed';
      case 'Cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  goToNewRequest(): void {
    this.router.navigate(['/routing-request']);
  }
}
